import { BookOpenIcon, KeyboardIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Link } from "~/components/Link"
import { KeyboardShortcutsDialog } from "~/components/KeyboardShortcutsDialog"
import { DiscordIcon, OrbitLogo } from "~/components/icons/logos"
import { Button, TooltipButton } from "~/components/ui/button"
import { SidebarTrigger, useSidebar } from "~/components/ui/sidebar"
import { UserMenu } from "~/components/sidebar/userMenu"
import { ORBIT_COLOR } from "~/config"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { cn } from "~/lib/utils"
import { RolePreviewControl } from "~/components/RolePreview"
import { ActivityExternalLink } from "~/components/ActivityExternalLink"

export function AppShellHeader({ children }: { children?: ReactNode }) {
	const { organization } = useOrganization()
	const { open, isMobile } = useSidebar()
	const { isPreviewing } = usePermission()
	const showPreview = (isMobile || !open) && isPreviewing

	return (
		<header className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 px-2 text-text-main md:grid-cols-[auto_minmax(0,1fr)_auto]">
			<div
				className={cn(
					"flex h-14 min-w-0 items-center gap-3 px-1",
					open && "md:w-[calc(var(--sidebar-width)-0.5rem)]"
				)}
			>
				<SidebarTrigger />
				<Link
					to={organization ? `/${organization.slug}` : "/"}
					className="flex items-center gap-2 font-semibold"
					aria-label="Orbit home"
				>
					<OrbitLogo fill={ORBIT_COLOR} width={22} height={24} />
					<span className={showPreview ? "hidden sm:inline" : undefined}>
						Orbit
					</span>
				</Link>
				{showPreview && (
					<div className="w-[calc(var(--sidebar-width)-1rem)] min-w-0 max-w-full">
						<RolePreviewControl />
					</div>
				)}
			</div>
			<div className="hidden min-w-0 md:col-start-2 md:row-start-1 md:block">
				{children}
			</div>
			<div className="col-start-2 row-start-1 flex shrink-0 items-center gap-1 md:col-start-3">
				<nav
					aria-label="Application links"
					className="hidden items-center gap-1 md:flex"
				>
					<TooltipButton
						asChild
						variant="ghost"
						size="icon-md"
						tooltip="Join Discord"
					>
						<ActivityExternalLink
							href="https://discord.gg/buape"
							target="_blank"
							rel="noreferrer noopener"
						>
							<DiscordIcon className="size-4" />
						</ActivityExternalLink>
					</TooltipButton>
					<TooltipButton
						asChild
						variant="ghost"
						size="icon-md"
						tooltip="Documentation"
					>
						<Link to="/docs">
							<BookOpenIcon className="size-4" />
						</Link>
					</TooltipButton>
				</nav>
				<UserMenu />
				<KeyboardShortcutsDialog>
					<Button
						variant="outline"
						size="icon"
						className={showPreview ? "hidden sm:inline-flex" : undefined}
						aria-label="Keyboard shortcuts"
						title="Keyboard shortcuts"
					>
						<KeyboardIcon className="size-4" />
					</Button>
				</KeyboardShortcutsDialog>
			</div>
		</header>
	)
}
